import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MeetingProgress, ProcessingStageInfo } from '../../../services/api.service';
import { useThrottledValue } from '../../../hooks/use-throttled-value.hook';
import { formatEtaLabel, formatMinutesLabel } from '../utils/processing-format';
import styles from './processing-panel.module.scss';

/** Задача L1: текст "Прошло/Осталось" не должен дёргаться на каждый тик поллинга (4с) —
 *  коммитим новое значение не чаще раза в 10с (округляем до минут). */
const TIMING_LABEL_THROTTLE_MS = 10000;

const FALLBACK_STAGES: ProcessingStageInfo[] = [];

interface ProcessingPanelProps {
  progress: MeetingProgress | null;
  /** Прогресс/стадия/сообщение до первого успешного ответа /progress (из meeting.status). */
  fallbackMessage?: string;
}

function StageIcon({ state }: { state: ProcessingStageInfo['state'] }) {
  if (state === 'done') {
    return (
      <span className={styles.stepIcon} data-state="done" aria-hidden>
        <svg viewBox="0 0 16 16" width="11" height="11">
          <path d="M3 8.5L6.2 11.5L13 4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    );
  }
  if (state === 'active') {
    return (
      <span className={styles.stepIcon} data-state="active" aria-hidden>
        <span className={styles.spinner} />
      </span>
    );
  }
  return <span className={styles.stepIcon} data-state="pending" aria-hidden />;
}

/** Живая панель прогресса обработки записи (задача L1) — степпер этапов, крупный
 *  прогресс-бар и "Прошло / Осталось" по elapsed_sec/eta_sec. Рендерится вместо/поверх
 *  контента страницы записи, пока встреча в статусе processing. */
const ProcessingPanel: React.FC<ProcessingPanelProps> = ({ progress, fallbackMessage }) => {
  const { t } = useTranslation();

  const percent = Math.max(0, Math.min(100, progress?.progress ?? 0));
  const message = progress?.message || fallbackMessage || t('processing.default-message');
  const stages = progress?.stages ?? FALLBACK_STAGES;

  const elapsedSec = progress?.elapsed_sec ?? null;
  const etaSec = progress?.eta_sec ?? null;

  const rawTimingLabel = useMemo(() => {
    const elapsedLabel = elapsedSec != null ? formatMinutesLabel(elapsedSec, t) : null;
    const etaLabel = formatEtaLabel(etaSec, t);
    return { elapsedLabel, etaLabel };
  }, [elapsedSec, etaSec, t]);

  const timingLabel = useThrottledValue(rawTimingLabel, TIMING_LABEL_THROTTLE_MS);

  return (
    <section className={styles.panel} aria-live="polite">
      <div className={styles.headRow}>
        <span className={styles.heading}>{t('processing.heading')}</span>
        <span className={styles.percent}>{percent}%</span>
      </div>

      <div className={styles.barTrack} role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
        <div className={styles.barFill} style={{ width: `${percent}%` }} />
      </div>

      <p className={styles.message}>{message}</p>

      <div className={styles.timing}>
        {timingLabel.elapsedLabel && (
          <span>{t('processing.elapsed')}: {timingLabel.elapsedLabel}</span>
        )}
        {timingLabel.elapsedLabel && <span className={styles.timingSep}>·</span>}
        <span>{t('processing.remaining')}: {timingLabel.etaLabel}</span>
      </div>

      {stages.length > 0 && (
        <ol className={styles.stepper}>
          {stages.map(stage => (
            <li
              key={stage.key}
              className={styles.step}
              data-state={stage.state}
            >
              <StageIcon state={stage.state} />
              <span className={styles.stepTitle}>{stage.title}</span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
};

export default ProcessingPanel;
